
import { useDispatch } from 'react-redux';
import { Checked } from '../../../../../assets/icon/svg';
import { MaterialSymbolsDeleteRounded } from '../../../../../assets/icon/iconify';
import { useIsCompleteProposalAs } from '../../../../../hooks/proposal/use-state-checker-proposal';
import { formatTinNumber } from '../../../../../utils/tin-number-formater.utils';
import { addAffiliation, clearAffiliations, removeAffiliation, setHasAffiliation, updateAffiliation } from '../../../../../features/gms-proposal-forms/basic-information/basic-information.slice';
import { customStyle, defaultStyle } from '../../../../../constant/custom-style';
import { useProposalTypeData } from '../../../../../hooks/proposal/use-proposal-type-data';

interface Props {
    isPreview?: boolean;
}

export default function ClientAffiliation({ isPreview = false }: Props) {
    const dispatch = useDispatch();
    const { affiliation: affiliations, hasAffiliation } = useProposalTypeData();
    const isComplete = useIsCompleteProposalAs();

    const handleToggle = () => {
        if (hasAffiliation) {
            dispatch(setHasAffiliation(false));
            dispatch(clearAffiliations());
        } else {
            dispatch(setHasAffiliation(true));
            dispatch(addAffiliation());
        }
    };

    const handleAddAffiliation = () => {
        dispatch(addAffiliation());
    };

    const handleRemoveAffiliation = (id: typeof affiliations[number]['id']) => {
        dispatch(removeAffiliation(id));
    };

    const handleUpdateAffiliation = (
        id: typeof affiliations[number]['id'],
        field: 'name' | 'address' | 'tinNumber',
        value: string
    ) => {
        dispatch(updateAffiliation({ id, field, value }));
    };

    const inputClass = (value: string) =>
        `w-full min-w-0 rounded-md px-3 py-2.5 text-sm outline-none transition focus:border-[#186cb2] focus:ring-2 focus:ring-[#186cb2]/10 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500 ${defaultStyle} ${value ? customStyle : ''}`;

    return (
        <div className="mt-2 min-w-0 space-y-2">
            {!isPreview && (
                <label className={`flex min-h-10 items-center gap-2 rounded-lg border px-3 py-2 transition ${!isComplete ? 'cursor-not-allowed bg-gray-100 text-gray-400' : 'cursor-pointer hover:bg-gray-50'} ${hasAffiliation ? 'border-[#186cb2]/30 bg-[#186cb2]/5' : 'border-gray-200 bg-white'}`}>
                    <input
                        type="checkbox"
                        checked={hasAffiliation}
                        onChange={handleToggle}
                        className="sr-only"
                        disabled={!isComplete}
                    />
                    <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${hasAffiliation ? 'border-[#186cb2] bg-[#186cb2]' : 'border-gray-400 bg-white'}`}>
                        {hasAffiliation && <Checked />}
                    </span>
                    <span className="text-xs font-medium">With Affiliation</span>
                </label>
            )}

            {hasAffiliation && (
                <div className="min-w-0 overflow-hidden rounded-lg border border-gray-200 bg-white">
                    {/* Header */}
                    <div className="flex flex-col gap-3 border-b border-gray-100 bg-gray-50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-4">
                        <div className="min-w-0">
                            <h3 className="text-sm font-semibold text-gray-800">
                                Affiliated Companies
                            </h3>
                            <p className="mt-0.5 text-xs text-gray-500">
                                Add all companies affiliated with the client.
                            </p>
                        </div>

                        <button
                            type="button"
                            onClick={handleAddAffiliation}
                            disabled={!isComplete}
                            className="inline-flex min-h-9 w-full shrink-0 items-center justify-center gap-1.5 rounded-md bg-[#186cb2] px-3 py-2 text-xs font-medium text-white transition hover:bg-[#155a96] disabled:cursor-not-allowed disabled:bg-gray-400 sm:w-auto"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 5v14" />
                                <path d="M5 12h14" />
                            </svg>
                            Add Affiliation
                        </button>
                    </div>

                    {/* Mobile and tablet: stacked cards */}
                    <div className="space-y-3 p-3 lg:hidden">
                        {affiliations.map((item, index) => (
                            <div key={item.id} className="min-w-0 rounded-lg border border-gray-200 bg-white p-3">
                                <div className="mb-3 flex items-center justify-between gap-3 border-b border-gray-100 pb-2">
                                    <div className="flex min-w-0 items-center gap-2">
                                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#186cb2]/10 text-xs font-semibold text-[#186cb2]">
                                            {index + 1}
                                        </span>
                                        <span className="truncate text-xs font-semibold text-gray-700">
                                            Affiliation {index + 1}
                                        </span>
                                    </div>

                                    <button
                                        type="button"
                                        disabled={affiliations.length === 1 || !isComplete}
                                        onClick={() => handleRemoveAffiliation(item.id)}
                                        aria-label={`Remove affiliation ${index + 1}`}
                                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
                                    >
                                        <MaterialSymbolsDeleteRounded color="#ef4444" height={19} width={19} />
                                    </button>
                                </div>

                                <div className="space-y-3">
                                    <div className="min-w-0">
                                        <label className="mb-1.5 block text-xs font-medium text-gray-600">
                                            Affiliation Name
                                        </label>
                                        <input
                                            type="text"
                                            value={item.name}
                                            className={inputClass(item.name)}
                                            placeholder="ABC Corporation"
                                            disabled={!isComplete}
                                            onChange={(e) => handleUpdateAffiliation(item.id, 'name', e.target.value)}
                                        />
                                    </div>

                                    <div className="min-w-0">
                                        <label className="mb-1.5 block text-xs font-medium text-gray-600">
                                            Address
                                        </label>
                                        <input
                                            type="text"
                                            value={item.address}
                                            className={inputClass(item.address)}
                                            placeholder="Company address"
                                            disabled={!isComplete}
                                            onChange={(e) => handleUpdateAffiliation(item.id, 'address', e.target.value)}
                                        />
                                    </div>

                                    <div className="min-w-0">
                                        <label className="mb-1.5 block text-xs font-medium text-gray-600">
                                            TIN Number
                                        </label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            value={formatTinNumber(item.tinNumber)}
                                            className={inputClass(item.tinNumber)}
                                            placeholder="000-000-000-00000"
                                            maxLength={17}
                                            disabled={!isComplete}
                                            onChange={(e) => {
                                                const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 14);
                                                handleUpdateAffiliation(item.id, 'tinNumber', digitsOnly);
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Desktop: table */}
                    <div className="hidden overflow-x-auto lg:block">
                        <table className="w-full min-w-[700px] table-fixed text-xs">
                            <thead>
                                <tr className="bg-gray-100 text-black">
                                    <th className="w-12 border px-3 py-3 text-center font-medium">#</th>
                                    <th className="w-[24%] border px-3 py-3 text-left font-medium">Affiliation Name</th>
                                    <th className="w-[38%] border px-3 py-3 text-left font-medium">Address</th>
                                    <th className="w-[24%] border px-3 py-3 text-left font-medium">TIN Number</th>
                                    <th className="w-14 border px-3 py-3 text-center font-medium">Action</th>
                                </tr>
                            </thead>

                            <tbody>
                                {affiliations.map((item, index) => (
                                    <tr key={item.id} className="transition hover:bg-gray-50/70">
                                        <td className="border p-2 text-center">
                                            <span className="inline-flex min-h-6 min-w-6 items-center justify-center rounded bg-[#186cb2]/10 px-1 text-[11px] font-medium text-[#186cb2]">
                                                {index + 1}
                                            </span>
                                        </td>

                                        <td className="border p-2">
                                            <input
                                                type="text"
                                                value={item.name}
                                                className={inputClass(item.name)}
                                                placeholder="ABC Corporation"
                                                disabled={!isComplete}
                                                onChange={(e) => handleUpdateAffiliation(item.id, 'name', e.target.value)}
                                            />
                                        </td>

                                        <td className="border p-2">
                                            <input
                                                type="text"
                                                value={item.address}
                                                className={inputClass(item.address)}
                                                placeholder="Company address"
                                                disabled={!isComplete}
                                                onChange={(e) => handleUpdateAffiliation(item.id, 'address', e.target.value)}
                                            />
                                        </td>

                                        <td className="border p-2">
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                value={formatTinNumber(item.tinNumber)}
                                                className={inputClass(item.tinNumber)}
                                                placeholder="000-000-000-00000"
                                                maxLength={17}
                                                disabled={!isComplete}
                                                onChange={(e) => {
                                                    const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 14);
                                                    handleUpdateAffiliation(item.id, 'tinNumber', digitsOnly);
                                                }}
                                            />
                                        </td>

                                        <td className="border p-2 text-center">
                                            <button
                                                type="button"
                                                disabled={affiliations.length === 1 || !isComplete}
                                                onClick={() => handleRemoveAffiliation(item.id)}
                                                aria-label={`Remove affiliation ${index + 1}`}
                                                className="inline-flex h-9 w-9 items-center justify-center rounded-md transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
                                            >
                                                <MaterialSymbolsDeleteRounded color="#ef4444" height={20} width={20} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="border-t border-gray-100 bg-gray-50 px-3 py-2">
                        <p className="text-[10px] text-gray-500">
                            {affiliations.length} {affiliations.length === 1 ? 'affiliation' : 'affiliations'} added
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
